/**
 * Keeping what somebody was typing, on their own machine.
 *
 * A ten thousand row import lost to a stray refresh is twenty minutes and a
 * genuinely bad afternoon, and the panel has no server to hold a half-finished
 * campaign — a draft only exists once it has been submitted through the gate.
 * So this is `localStorage`, and it is honest about being per-device.
 *
 * **It is a convenience, never a source of truth.** The saved copy can be
 * absent for reasons that are nobody's fault — a private window, cleared site
 * data, a different browser, a machine where storage throws on access — so
 * every read is wrapped and every failure is the same answer: nothing to
 * restore. What is confirmed and sent is always what the database was told,
 * which is the property `confirm.ts` already relies on.
 *
 * The encode/decode pair is pure and exported so it can be tested without a
 * browser; the two functions that touch storage are the thin part.
 */

import { EMPTY_INPUT, type CampaignInput } from '../domain/campaign-input.ts';

const KEY = 'promo-sms.draft';

/**
 * A day.
 *
 * Long enough to survive a browser crash, an accidental close, or going home
 * and coming back. Long enough that restoring last month's half-written
 * campaign would be a surprise rather than a rescue, which is the failure this
 * bound exists to prevent.
 */
export const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface StoredDraft {
  readonly savedAt: number;
  readonly input: CampaignInput;
}

export function encodeDraft(input: CampaignInput, savedAt: number = Date.now()): string {
  return JSON.stringify({ savedAt, input });
}

/**
 * Reads a stored draft, or nothing.
 *
 * Every way this can go wrong returns `undefined` rather than throwing: absent,
 * unparseable, written by an older bundle with a different shape, or simply too
 * old. A restore that fails must never be louder than the screen it was trying
 * to help.
 */
export function decodeDraft(
  raw: string | null | undefined,
  now: number = Date.now(),
  maxAgeMs: number = DRAFT_MAX_AGE_MS,
): CampaignInput | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }

  if (typeof parsed !== 'object' || parsed === null) return undefined;
  const stored = parsed as Partial<StoredDraft>;

  if (typeof stored.savedAt !== 'number' || !Number.isFinite(stored.savedAt)) return undefined;
  if (now - stored.savedAt > maxAgeMs) return undefined;
  if (typeof stored.input !== 'object' || stored.input === null) return undefined;

  // Every field is taken from the stored copy only when it is the right type,
  // so a draft written by an older bundle restores what still fits and defaults
  // the rest rather than producing a screen with `undefined` in a textarea.
  const input = stored.input as Partial<CampaignInput>;
  const restored: CampaignInput = {
    ...EMPTY_INPUT,
    ...(typeof input.mode === 'string' && ['pairs', 'blocks', 'file'].includes(input.mode)
      ? { mode: input.mode as CampaignInput['mode'] }
      : {}),
    ...(typeof input.pairs === 'string' ? { pairs: input.pairs } : {}),
    ...(typeof input.blocks === 'string' ? { blocks: input.blocks } : {}),
    ...(typeof input.template === 'string' ? { template: input.template } : {}),
    ...(typeof input.imported === 'string' ? { imported: input.imported } : {}),
    ...(typeof input.importName === 'string' ? { importName: input.importName } : {}),
    ...(typeof input.separator === 'string' ? { separator: input.separator as CampaignInput['separator'] } : {}),
  };

  return isEmpty(restored) ? undefined : restored;
}

/** Nothing worth restoring. Offering to bring back a blank screen is noise. */
export function isEmpty(input: CampaignInput): boolean {
  return (
    input.pairs.trim() === '' &&
    input.blocks.trim() === '' &&
    input.template.trim() === '' &&
    input.imported.trim() === ''
  );
}

// ---------------------------------------------------------------------------
// The two that touch the browser

export function saveDraft(input: CampaignInput): void {
  try {
    if (isEmpty(input)) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, encodeDraft(input));
  } catch {
    // Storage full, or blocked entirely. Losing the convenience is not worth
    // interrupting somebody who is in the middle of typing.
  }
}

export function loadDraft(): CampaignInput | undefined {
  try {
    return decodeDraft(window.localStorage.getItem(KEY));
  } catch {
    return undefined;
  }
}

export function clearDraft(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing to do, and nothing worth saying.
  }
}
